import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    Button,
    Grid,
    Typography,
    TextField,
    MenuItem,
    Box,
    Stack,
    Divider,
    Alert,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    IconButton,
    Backdrop,
    Autocomplete
} from '@mui/material';
import { styled, alpha } from '@mui/material/styles';
import { useDispatch, useSelector } from 'react-redux';
import { LoadingButton } from '@mui/lab';
import { useSnackbar } from 'notistack';
import Iconify from '../../../components/Iconify';
import { getInstrumentList } from '../../../redux/slices/trades';
import { createHedgingView } from '../../../redux/slices/operator';

// Helper functions for styling
const getBorderColor = (side, theme) => {
    if (side === 'BUY') {
        return alpha(theme.palette.success.main, 0.2);
    }
    if (side === 'SELL') {
        return alpha(theme.palette.error.main, 0.2);
    }
    return alpha(theme.palette.primary.main, 0.2);
};

const getBackgroundColor = (side, theme) => {
    if (side === 'BUY') {
        return alpha(theme.palette.success.main, 0.05);
    }
    if (side === 'SELL') {
        return alpha(theme.palette.error.main, 0.05);
    }
    return alpha(theme.palette.primary.main, 0.05);
};

const getCardHeaderColor = (side) => {
    if (side === 'BUY') {
        return '#e8f5e8';
    }
    if (side === 'SELL') {
        return '#fef2f2';
    }
    return 'rgba(33, 150, 243, 0.05)';
};

// Styled components for ultra-compact modal design
const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiDialog-paper': {
        borderRadius: theme.shape.borderRadius * 2,
        maxWidth: '95vw',
        width: '100%',
        maxHeight: '95vh',
        margin: theme.spacing(0.5),
        [theme.breakpoints.up('sm')]: {
            maxWidth: 800,
            margin: theme.spacing(1),
        },
        [theme.breakpoints.up('md')]: {
            maxWidth: 900,
        },
        overflow: 'visible',
        boxShadow: theme.shadows[24],
    },
}));

const CompactCard = styled(Card)(({ theme, side }) => ({
    backgroundColor: getBackgroundColor(side, theme),
    borderRadius: theme.shape.borderRadius,
    border: `1px solid ${getBorderColor(side, theme)}`,
    transition: theme.transitions.create(['box-shadow'], {
        duration: theme.transitions.duration.shorter,
    }),
    boxShadow: theme.shadows[2],
    overflow: 'visible',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    '&:hover': {
        boxShadow: theme.shadows[4],
    },
}));

const CompactCardHeader = styled(Box)(({ theme, side }) => ({
    padding: theme.spacing(1.2, 2),
    backgroundColor: getCardHeaderColor(side),
    borderBottom: `1px solid ${alpha(theme.palette.divider, 0.08)}`,
    borderRadius: `${theme.shape.borderRadius * 2.5}px ${theme.shape.borderRadius * 2.5}px 0 0`,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    minHeight: 40,
}));

const SuperCompactTextField = styled(TextField)(({ theme }) => ({
    '& .MuiOutlinedInput-root': {
        minHeight: 40,
        borderRadius: theme.shape.borderRadius * 1.5,
        fontSize: '0.8rem',
        backgroundColor: alpha(theme.palette.background.neutral, 0.15),
        '& fieldset': {
            borderColor: alpha(theme.palette.divider, 0.2),
        },
        '&:hover fieldset': {
            borderColor: theme.palette.primary.light,
        },
        '&.Mui-focused fieldset': {
            borderColor: theme.palette.primary.main,
            borderWidth: 2,
        },
    },
    '& .MuiInputLabel-root': {
        fontSize: '0.8rem',
        fontWeight: 500,
        transform: 'translate(14px, 12px) scale(1)',
        backgroundColor: 'transparent',
        '&.Mui-focused, &.MuiFormLabel-filled': {
            transform: 'translate(14px, -9px) scale(0.75)',
            backgroundColor: theme.palette.background.paper,
            padding: '0 4px',
        },
    },
    '& .MuiSelect-select': {
        padding: theme.spacing(1, 1.5),
    },
    '& .MuiInputBase-input': {
        padding: theme.spacing(1, 1.5),
    },
}));

const SearchDropdown = styled(Box)(({ theme }) => ({
    position: 'fixed',
    maxHeight: 180,
    overflow: 'auto',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius * 1.5,
    backgroundColor: theme.palette.background.paper,
    zIndex: 999999,
    boxShadow: theme.shadows[16],
    minWidth: 200,
    '& .MuiBox-root:last-child': {
        borderBottom: 'none',
    },
}));

const PreviewCard = styled(Card)(({ theme }) => ({
    backgroundColor: alpha(theme.palette.info.main, 0.02),
    border: `1px solid ${alpha(theme.palette.info.main, 0.12)}`,
    borderRadius: theme.shape.borderRadius * 2.5,
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    boxShadow: theme.shadows[2],
}));

const HedgingViewModal = ({ open, onClose }) => {
    const dispatch = useDispatch();
    const { enqueueSnackbar } = useSnackbar();

    // Redux state
    const { instrumentData, isLoading } = useSelector((state) => state?.trade);
    const { isLoading: operatorLoading } = useSelector((state) => state?.operator);

    // Local state
    const [formData, setFormData] = useState({
        firstScript: {
            side: 'BUY',
            scriptId: '',
            scriptName: '',
            quantity: '',
            price: '',
            multiplier: '1'
        },
        secondScript: {
            side: 'SELL',
            scriptId: '',
            scriptName: '',
            quantity: '',
            price: '',
            multiplier: '1'
        }
    });
    const [isSubmitting, setIsSubmitting] = useState(false);

    // Available instruments from Redux
    const instruments = instrumentData?.docs || [];

    useEffect(() => {
        // Fetch instruments when component mounts
        if (open) {
            dispatch(getInstrumentList());
        }
    }, [dispatch, open]);

    const handleInputChange = (scriptType, field, value) => {
        setFormData(prev => ({
            ...prev,
            [scriptType]: {
                ...prev[scriptType],
                [field]: value
            }
        }));
    };

    const handleScriptSelect = (scriptType, script) => {
        const selectedValue = script.tradingsymbol;

        // console.log('Script selected:', { scriptType, script: selectedValue, scriptId: script._id });

        setFormData(prev => ({
            ...prev,
            [scriptType]: {
                ...prev[scriptType],
                scriptId: script._id,
                scriptName: selectedValue
            }
        }));
    };

    const handleSaveHedgingView = async () => {
        if (!isFormValid()) {
            enqueueSnackbar('Please fill all required fields with valid data', { variant: 'error' });
            return;
        }

        // Check for opposite sides validation
        if (!sideValidation.isValid) {
            enqueueSnackbar(sideValidation.message, { variant: 'error' });
            return;
        }

        setIsSubmitting(true);

        try {
            // Transform form data to match API payload structure
            const payload = {
                script1: {
                    scriptId: formData.firstScript.scriptId,
                    side: formData.firstScript.side,
                    quantity: parseInt(formData.firstScript.quantity, 10),
                    price: parseFloat(formData.firstScript.price),
                    multiplier: parseFloat(formData.firstScript.multiplier)
                },
                script2: {
                    scriptId: formData.secondScript.scriptId,
                    side: formData.secondScript.side,
                    quantity: parseInt(formData.secondScript.quantity, 10),
                    price: parseFloat(formData.secondScript.price),
                    multiplier: parseFloat(formData.secondScript.multiplier)
                }
            };

            // console.log('Creating Hedging View with payload:', payload);

            const result = await dispatch(createHedgingView(payload));

            if (result.success) {
                enqueueSnackbar(result.message || 'Hedging view created successfully!', { variant: 'success' });

                // Reset form after successful creation
                setFormData({
                    firstScript: { side: 'BUY', scriptId: '', scriptName: '', quantity: '', price: '', multiplier: '1' },
                    secondScript: { side: 'SELL', scriptId: '', scriptName: '', quantity: '', price: '', multiplier: '1' }
                });
                onClose();
            } else {
                enqueueSnackbar(result.message || 'Failed to create hedging view', { variant: 'error' });
            }
        } catch (error) {
            console.error('Error creating hedging view:', error);
            enqueueSnackbar('An unexpected error occurred. Please try again.', { variant: 'error' });
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        // Reset form when closing
        setFormData({
            firstScript: { side: 'BUY', scriptId: '', scriptName: '', quantity: '', price: '', multiplier: '1' },
            secondScript: { side: 'SELL', scriptId: '', scriptName: '', quantity: '', price: '', multiplier: '1' }
        });
        setIsSubmitting(false);
        onClose();
    };

    const isFormValid = () => {
        return formData.firstScript.scriptId &&
            formData.firstScript.quantity &&
            formData.firstScript.price &&
            formData.firstScript.multiplier &&
            formData.secondScript.scriptId &&
            formData.secondScript.quantity &&
            formData.secondScript.price &&
            formData.secondScript.multiplier;
    };

    // Validation for opposite sides
    const validateOppositeSides = () => {
        if (!formData.firstScript.side || !formData.secondScript.side) {
            return { isValid: true, message: '' }; // No validation if sides not selected yet
        }

        if (formData.firstScript.side === formData.secondScript.side) {
            return {
                isValid: false,
                message: 'Both scripts must have opposite directions for hedging. One should be BUY and other should be SELL.'
            };
        }

        return { isValid: true, message: '' };
    };

    const sideValidation = validateOppositeSides();

    return (
        <>
            <StyledDialog
                open={open}
                onClose={handleClose}
                maxWidth={false}
                BackdropComponent={Backdrop}
                BackdropProps={{
                    sx: { backgroundColor: 'rgba(0, 0, 0, 0.8)' }
                }}
            >
                <DialogTitle
                    sx={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        py: 1.5,
                        px: 2.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderTopLeftRadius: (theme) => theme.shape.borderRadius * 2,
                        borderTopRightRadius: (theme) => theme.shape.borderRadius * 2,
                        mb: 0.5,
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Iconify icon="mdi:plus-circle" />
                        <Typography variant="h6" fontWeight={700}>
                            Create Hedging View
                        </Typography>
                    </Box>
                    <IconButton
                        onClick={handleClose}
                        sx={{ color: 'white' }}
                    >
                        <Iconify icon="eva:close-fill" />
                    </IconButton>
                </DialogTitle>

                <DialogContent sx={{ p: 1, pt: 0.5, pb: 0 }}>
                    <Grid container spacing={0.5}>
                        {/* First Script Card */}
                        <Grid item xs={12} sm={6} md={4.5}>
                            <CompactCard side={formData.firstScript.side}>
                                <CompactCardHeader side={formData.firstScript.side}>
                                    <Iconify
                                        icon={formData.firstScript.side === 'BUY' ? "mdi:trending-up" : "mdi:trending-down"}
                                        sx={{
                                            color: formData.firstScript.side === 'BUY' ? 'success.main' : 'error.main',
                                            fontSize: 18
                                        }}
                                    />
                                    <Typography
                                        variant="subtitle2"
                                        color={formData.firstScript.side === 'BUY' ? 'success.main' : 'error.main'}
                                        fontWeight={700}
                                        sx={{ fontSize: '0.85rem' }}
                                    >
                                        First Script
                                    </Typography>
                                </CompactCardHeader>
                                <CardContent sx={{ p: 2.5, flexGrow: 1 }}>
                                    <Stack spacing={2}>
                                        {/* Script Name Row */}
                                        <Box sx={{ position: 'relative' }} className="script-search-container">
                                            <Autocomplete
                                                fullWidth
                                                size="small"
                                                options={instruments}
                                                getOptionLabel={(option) => option.tradingsymbol || ''}
                                                value={instruments.find(inst => inst._id === formData.firstScript.scriptId) || null}
                                                onChange={(event, newValue) => {
                                                    if (newValue) {
                                                        handleScriptSelect('firstScript', newValue);
                                                    } else {
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            firstScript: { ...prev.firstScript, scriptId: '', scriptName: '' }
                                                        }));
                                                    }
                                                }}
                                                renderInput={(params) => (
                                                    <SuperCompactTextField
                                                        {...params}
                                                        placeholder="Search for script..."
                                                        InputProps={{
                                                            ...params.InputProps,
                                                            startAdornment: (
                                                                <Iconify
                                                                    icon="eva:search-fill"
                                                                    sx={{ color: 'text.disabled', mr: 1, fontSize: 16 }}
                                                                />
                                                            ),
                                                            endAdornment: (
                                                                <>
                                                                    {formData.firstScript.scriptId && (
                                                                        <Iconify
                                                                            icon="eva:checkmark-circle-fill"
                                                                            sx={{ color: 'success.main', ml: 1, fontSize: 16 }}
                                                                        />
                                                                    )}
                                                                    {params.InputProps.endAdornment}
                                                                </>
                                                            ),
                                                        }}
                                                    />
                                                )}
                                                renderOption={(props, option) => (
                                                    <Box {...props} key={option._id}>
                                                        <Box>
                                                            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                                                                {option.tradingsymbol}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                                                {option.name} • {option.exchange?.name}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                )}
                                            />
                                        </Box>

                                        {/* Multiplier Row */}
                                        <SuperCompactTextField
                                            fullWidth
                                            type="number"
                                            label="Multiplier"
                                            placeholder="1.0"
                                            value={formData.firstScript.multiplier}
                                            onChange={(e) => handleInputChange('firstScript', 'multiplier', e.target.value)}
                                            size="small"
                                            InputProps={{
                                                inputProps: { min: 0, step: 0.01 }
                                            }}
                                        />

                                        {/* Side, Quantity and Price Row */}
                                        <Grid container spacing={0.2}>
                                            <Grid item xs={4}>
                                                <SuperCompactTextField
                                                    select
                                                    fullWidth
                                                    label="Side"
                                                    value={formData.firstScript.side}
                                                    onChange={(e) => handleInputChange('firstScript', 'side', e.target.value)}
                                                    size="small"
                                                >
                                                    <MenuItem value="BUY">
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <Iconify icon="mdi:trending-up" sx={{ color: 'success.main', fontSize: 16 }} />
                                                            <Typography sx={{ fontSize: '0.8rem' }}>BUY</Typography>
                                                        </Box>
                                                    </MenuItem>
                                                    <MenuItem value="SELL">
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <Iconify icon="mdi:trending-down" sx={{ color: 'error.main', fontSize: 16 }} />
                                                            <Typography sx={{ fontSize: '0.8rem' }}>SELL</Typography>
                                                        </Box>
                                                    </MenuItem>
                                                </SuperCompactTextField>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <SuperCompactTextField
                                                    fullWidth
                                                    type="number"
                                                    label="Quantity"
                                                    placeholder="Qty"
                                                    value={formData.firstScript.quantity}
                                                    onChange={(e) => handleInputChange('firstScript', 'quantity', e.target.value)}
                                                    size="small"
                                                    InputProps={{
                                                        inputProps: { min: 1 }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={4}>
                                                <SuperCompactTextField
                                                    fullWidth
                                                    type="number"
                                                    label="Price"
                                                    placeholder="0.00"
                                                    value={formData.firstScript.price}
                                                    onChange={(e) => handleInputChange('firstScript', 'price', e.target.value)}
                                                    size="small"
                                                    InputProps={{
                                                        inputProps: { min: 0, step: 0.01 }
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Stack>
                                </CardContent>
                            </CompactCard>
                        </Grid>

                        {/* Second Script Card */}
                        <Grid item xs={12} sm={6} md={4.5}>
                            <CompactCard side={formData.secondScript.side}>
                                <CompactCardHeader side={formData.secondScript.side}>
                                    <Iconify
                                        icon={formData.secondScript.side === 'BUY' ? "mdi:trending-up" : "mdi:trending-down"}
                                        sx={{
                                            color: formData.secondScript.side === 'BUY' ? 'success.main' : 'error.main',
                                            fontSize: 18
                                        }}
                                    />
                                    <Typography
                                        variant="subtitle2"
                                        color={formData.secondScript.side === 'BUY' ? 'success.main' : 'error.main'}
                                        fontWeight={700}
                                        sx={{ fontSize: '0.85rem' }}
                                    >
                                        Second Script
                                    </Typography>
                                </CompactCardHeader>
                                <CardContent sx={{ p: 2.5, flexGrow: 1 }}>
                                    <Stack spacing={2}>
                                        {/* Script Name Row */}
                                        <Box sx={{ position: 'relative' }} className="script-search-container">
                                            <Autocomplete
                                                fullWidth
                                                size="small"
                                                options={instruments}
                                                getOptionLabel={(option) => option.tradingsymbol || ''}
                                                value={instruments.find(inst => inst._id === formData.secondScript.scriptId) || null}
                                                onChange={(event, newValue) => {
                                                    if (newValue) {
                                                        handleScriptSelect('secondScript', newValue);
                                                    } else {
                                                        setFormData(prev => ({
                                                            ...prev,
                                                            secondScript: { ...prev.secondScript, scriptId: '', scriptName: '' }
                                                        }));
                                                    }
                                                }}
                                                renderInput={(params) => (
                                                    <SuperCompactTextField
                                                        {...params}
                                                        placeholder="Search for script..."
                                                        InputProps={{
                                                            ...params.InputProps,
                                                            startAdornment: (
                                                                <Iconify
                                                                    icon="eva:search-fill"
                                                                    sx={{ color: 'text.disabled', mr: 1, fontSize: 16 }}
                                                                />
                                                            ),
                                                            endAdornment: (
                                                                <>
                                                                    {formData.secondScript.scriptId && (
                                                                        <Iconify
                                                                            icon="eva:checkmark-circle-fill"
                                                                            sx={{ color: 'success.main', ml: 1, fontSize: 16 }}
                                                                        />
                                                                    )}
                                                                    {params.InputProps.endAdornment}
                                                                </>
                                                            ),
                                                        }}
                                                    />
                                                )}
                                                renderOption={(props, option) => (
                                                    <Box {...props} key={option._id}>
                                                        <Box>
                                                            <Typography variant="body2" fontWeight={600} sx={{ fontSize: '0.8rem' }}>
                                                                {option.tradingsymbol}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                                                {option.name} • {option.exchange?.name}
                                                            </Typography>
                                                        </Box>
                                                    </Box>
                                                )}
                                            />
                                        </Box>

                                        {/* Multiplier Row */}
                                        <SuperCompactTextField
                                            fullWidth
                                            type="number"
                                            label="Multiplier"
                                            placeholder="1.0"
                                            value={formData.secondScript.multiplier}
                                            onChange={(e) => handleInputChange('secondScript', 'multiplier', e.target.value)}
                                            size="small"
                                            InputProps={{
                                                inputProps: { min: 0, step: 0.01 }
                                            }}
                                        />

                                        {/* Side, Quantity and Price Row */}
                                        <Grid container spacing={0.2}>
                                            <Grid item xs={4}>
                                                <SuperCompactTextField
                                                    select
                                                    fullWidth
                                                    label="Side"
                                                    value={formData.secondScript.side}
                                                    onChange={(e) => handleInputChange('secondScript', 'side', e.target.value)}
                                                    size="small"
                                                >
                                                    <MenuItem value="BUY">
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <Iconify icon="mdi:trending-up" sx={{ color: 'success.main', fontSize: 16 }} />
                                                            <Typography sx={{ fontSize: '0.8rem' }}>BUY</Typography>
                                                        </Box>
                                                    </MenuItem>
                                                    <MenuItem value="SELL">
                                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                            <Iconify icon="mdi:trending-down" sx={{ color: 'error.main', fontSize: 16 }} />
                                                            <Typography sx={{ fontSize: '0.8rem' }}>SELL</Typography>
                                                        </Box>
                                                    </MenuItem>
                                                </SuperCompactTextField>
                                            </Grid>
                                            <Grid item xs={4}>
                                                <SuperCompactTextField
                                                    fullWidth
                                                    type="number"
                                                    label="Quantity"
                                                    placeholder="Qty"
                                                    value={formData.secondScript.quantity}
                                                    onChange={(e) => handleInputChange('secondScript', 'quantity', e.target.value)}
                                                    size="small"
                                                    InputProps={{
                                                        inputProps: { min: 1 }
                                                    }}
                                                />
                                            </Grid>
                                            <Grid item xs={4}>
                                                <SuperCompactTextField
                                                    fullWidth
                                                    type="number"
                                                    label="Price"
                                                    placeholder="0.00"
                                                    value={formData.secondScript.price}
                                                    onChange={(e) => handleInputChange('secondScript', 'price', e.target.value)}
                                                    size="small"
                                                    InputProps={{
                                                        inputProps: { min: 0, step: 0.01 }
                                                    }}
                                                />
                                            </Grid>
                                        </Grid>
                                    </Stack>
                                </CardContent>
                            </CompactCard>
                        </Grid>

                        {/* Compact Preview Results */}
                        <Grid item xs={12} sm={12} md={3}>
                            <PreviewCard>
                                <CompactCardHeader bgcolor="rgba(33, 150, 243, 0.05)">
                                    <Iconify icon="mdi:eye" sx={{ color: 'info.main', fontSize: 18 }} />
                                    <Typography variant="subtitle2" color="info.main" fontWeight={700} sx={{ fontSize: '0.85rem' }}>
                                        Preview
                                    </Typography>
                                </CompactCardHeader>
                                <CardContent sx={{ p: 2.5, flexGrow: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                                    {isFormValid() ? (
                                        <Stack spacing={1}>
                                            {/* <Alert severity="info" sx={{ fontSize: '0.75rem', py: 0.5, px: 1.5 }}>
                                                Review before saving
                                            </Alert> */}

                                            <Box>
                                                <Typography variant="caption" color={formData.firstScript.side === 'BUY' ? 'success.main' : 'error.main'} fontWeight={600} sx={{ fontSize: '0.75rem' }}>
                                                    First: {formData.firstScript.side}
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.3 }}>
                                                    {formData.firstScript.quantity} × {formData.firstScript.scriptName}
                                                </Typography>
                                                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                                    Price: ₹{formData.firstScript.price} | Multiplier: {formData.firstScript.multiplier}
                                                </Typography>
                                            </Box>

                                            <Divider sx={{ my: 0.5 }} />

                                            <Box>
                                                <Typography variant="caption" color={formData.secondScript.side === 'BUY' ? 'success.main' : 'error.main'} fontWeight={600} sx={{ fontSize: '0.75rem' }}>
                                                    Second: {formData.secondScript.side}
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontSize: '0.8rem', lineHeight: 1.3 }}>
                                                    {formData.secondScript.quantity} × {formData.secondScript.scriptName}
                                                </Typography>
                                                <Typography variant="caption" sx={{ fontSize: '0.7rem', color: 'text.secondary' }}>
                                                    Price: ₹{formData.secondScript.price} | Multiplier: {formData.secondScript.multiplier}
                                                </Typography>
                                            </Box>

                                            {!sideValidation.isValid && (
                                                <>
                                                    <Divider sx={{ my: 0.5 }} />
                                                    <Alert severity="warning" sx={{ fontSize: '0.7rem', py: 0.3, px: 1 }}>
                                                        Sides must be opposite!
                                                    </Alert>
                                                </>
                                            )}
                                        </Stack>
                                    ) : (
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 1,
                                            // py: 1 
                                        }}>
                                            <Iconify
                                                icon="mdi:information-outline"
                                                sx={{ fontSize: 20, color: 'text.disabled' }}
                                            />
                                            <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.75rem' }}>
                                                Fill both scripts to preview
                                            </Typography>
                                        </Box>
                                    )}
                                </CardContent>
                            </PreviewCard>
                        </Grid>
                    </Grid>

                    {/* Validation Error Display */}
                    {!sideValidation.isValid && (
                        <Box sx={{ mt: 2, px: 1 }}>
                            <Alert severity="error" sx={{ fontSize: '0.8rem' }}>
                                {sideValidation.message}
                            </Alert>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{
                    p: 1,
                    pt: 0,
                    // mt: -4,
                    '&.MuiDialogActions-root': {
                        padding: '0 1rem 1rem 1rem',
                        margin: 0
                    }
                }}>
                    <Button
                        variant="outlined"
                        onClick={handleClose}
                        startIcon={<Iconify icon="eva:close-fill" />}
                        sx={{ minWidth: 110, fontSize: '0.85rem', borderRadius: 2 }}
                    >
                        Cancel
                    </Button>
                    <LoadingButton
                        variant="contained"
                        onClick={handleSaveHedgingView}
                        disabled={!isFormValid() || !sideValidation.isValid || isSubmitting}
                        loading={isSubmitting || operatorLoading}
                        startIcon={<Iconify icon="eva:save-fill" />}
                        sx={{
                            minWidth: 140,
                            fontSize: '0.85rem',
                            borderRadius: 2,
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            '&:hover': {
                                background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
                            },
                            '&:disabled': {
                                background: 'rgba(0, 0, 0, 0.12)',
                            }
                        }}
                    >
                        Save Hedging View
                    </LoadingButton>
                </DialogActions>
            </StyledDialog>
        </>
    );
};

export default HedgingViewModal;
